import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search, Truck, PackageCheck, Users, Wallet } from "lucide-react";
import { apiClient } from "../../api/client";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { unwrapArray } from "../../lib/api-envelope";
import { tenantTodayDate, toCalendarDateString } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { fmtCurrency, fmtHours, fmtQty, parseMoney, sumMoney } from "../../lib/display-labels";
import type {
  TruckingBolSummaryRow,
  TruckingCustomerRevenueRow,
  TruckingDriverPayRow,
  TruckingReportEnvelope,
} from "../../types/trucking-reports";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { LOADS, missingRateNote } from "../../lib/missing-rate-note";

/**
 * Trucking Reports, bound to what /trucking/reports/* ACTUALLY returns.
 *
 * READ THIS BEFORE "FIXING" A COLUMN. Every one of the three tables here used
 * to read keys the API has never sent, and the nearest key on the payload is
 * not always the same fact. Each binding below was checked against the
 * handler in fieldiq-api/src/routes/trucking-reports.routes.ts:
 *
 * BOL SUMMARY (/bol-summary) is per CUSTOMER, not per BOL. It sends
 *   `customer_name`, `total_loads`, `cancelled_loads`, `total_value`,
 *   `unrated_count`, `total_weight`. The table used to draw a per-BOL register
 *   (BOL #, date, driver, route, status) from keys that do not exist, so every
 *   cell was a dash and Amount read $0.00.
 *   - `total_loads` counts every load SCHEDULED in the range, cancelled ones
 *     included; `cancelled_loads` says how many of them were cancelled.
 *   - `total_weight` is the SUM of `weight_tickets.net_weight_kg` on those
 *     loads, in kilograms. Shown as net weight ticketed.
 *   - `total_value` IS DELIVERED MONEY ONLY (api#1106): the sum over the
 *     delivered and signed loads, `delivered_loads` of them. Before that it
 *     was null whenever ANY load in the range had no rate, and a booked load
 *     is not rated until it is worked, so a range reaching into next week read
 *     "—" for every customer while three loads were already invoiced. It is
 *     null now only when a delivered or signed load itself has no amount, with
 *     `delivered_unrated_count` saying how many: a dash and that count, never a
 *     partial sum. A customer with nothing delivered reads $0.00 beside a
 *     Delivered of 0, which is the true figure, not an absent one.
 *   - `unrated_count` (any status, not cancelled) is not shown: it mixes the
 *     open loads that are not priced yet with the delivered ones that should
 *     be, and only the second kind withholds a figure.
 *
 * DRIVER PAY (/driver-pay) is per driver, summed from trucking_payroll_entries
 *   (every column NOT NULL, so no row is silently skipped).
 *   - THE WINDOW IS NOT THE PICKER'S. It includes only pay periods that start
 *     AND end inside the range; a period straddling either end is left out.
 *     The figures are right for that scope, so they are shown and the panel
 *     says the scope out loud. Filed on #815.
 *   - There is no load count on this route, so the old Loads column is gone.
 *   - Shop and standby earnings are sent and are part of gross pay, so they
 *     are shown; the old table hid them and its columns did not reach gross.
 *
 * CUSTOMER REVENUE (/customer-revenue) counts loads DELIVERED or SIGNED in the
 *   range per customer, with `total_revenue` and `avg_per_load`. Since api#851
 *   both are null when any of those loads has no rate, with `unrated_count`; a
 *   dash and the count, as above. There is no BOL count (every load IS a BOL,
 *   so it would only repeat the load count) and no weight on this route.
 *
 * Export CSV is gone: no route answers `format=csv`, so it saved a JSON body
 * under a .csv name.
 */

type ReportTab = "bol" | "driver_pay" | "customer_loads";

const TABS: { key: ReportTab; label: string }[] = [
  { key: "bol", label: "BOL Summary" },
  { key: "driver_pay", label: "Driver Pay" },
  { key: "customer_loads", label: "Customer Revenue" },
];

interface BolSummaryRow {
  customer_name: string | null;
  total_loads: number | null;
  cancelled_loads: number | null;
  delivered_loads: number | null;
  total_value: number | null;
  delivered_unrated_count: number | null;
  total_weight: number | null;
}
interface DriverPayRow {
  driver_id: string;
  driver_name: string | null;
  total_regular_hours: number | null;
  total_ot_hours: number | null;
  total_bol_earnings: number | null;
  total_hourly_earnings: number | null;
  total_shop_earnings: number | null;
  total_standby_earnings: number | null;
  total_per_diem: number | null;
  total_gross_pay: number | null;
}
interface CustomerLoadsRow {
  customer_name: string | null;
  load_count: number | null;
  total_revenue: number | null;
  avg_per_load: number | null;
  unrated_count: number | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));

const toBol = (r: Record<string, unknown>): BolSummaryRow => ({
  customer_name: str(r.customer_name),
  total_loads: parseMoney(r.total_loads),
  cancelled_loads: parseMoney(r.cancelled_loads),
  delivered_loads: parseMoney(r.delivered_loads),
  total_value: parseMoney(r.total_value),
  delivered_unrated_count: parseMoney(r.delivered_unrated_count),
  total_weight: parseMoney(r.total_weight),
});
const toPay = (r: Record<string, unknown>): DriverPayRow => ({
  driver_id: String(r.driver_id ?? ""),
  driver_name: str(r.driver_name),
  total_regular_hours: parseMoney(r.total_regular_hours),
  total_ot_hours: parseMoney(r.total_ot_hours),
  total_bol_earnings: parseMoney(r.total_bol_earnings),
  total_hourly_earnings: parseMoney(r.total_hourly_earnings),
  total_shop_earnings: parseMoney(r.total_shop_earnings),
  total_standby_earnings: parseMoney(r.total_standby_earnings),
  total_per_diem: parseMoney(r.total_per_diem),
  total_gross_pay: parseMoney(r.total_gross_pay),
});
const toCustomer = (r: Record<string, unknown>): CustomerLoadsRow => ({
  customer_name: str(r.customer_name),
  load_count: parseMoney(r.load_count),
  total_revenue: parseMoney(r.total_revenue),
  avg_per_load: parseMoney(r.avg_per_load),
  unrated_count: parseMoney(r.unrated_count),
});

/** A sum of API counts across rows, all-or-nothing: a dash if the list did not arrive or any row lacks the count. */
const total = (rows: { [k: string]: unknown }[] | undefined, key: string) =>
  rows ? sumMoney(rows.map((r) => r[key])) : null;

/**
 * The default range: the last 30 of the TENANT's days (companies.time_zone),
 * ending on the tenant's today. Computed when the screen mounts, not when the
 * module loads, so a tab left open overnight does not keep yesterday's range.
 * '' seeds the inputs only when no zone is known.
 */
const defaultRange = (timeZone: string | null): { start: string; end: string } => {
  const today = tenantTodayDate(timeZone);
  if (!today) return { start: "", end: "" };
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
  return { start: toCalendarDateString(start), end: toCalendarDateString(today) };
};

const TruckingReportsScreen = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ReportTab>("bol");
  const timeZone = useTenantTimeZone();
  const [startDate, setStartDate] = useState(() => defaultRange(timeZone).start);
  const [endDate, setEndDate] = useState(() => defaultRange(timeZone).end);
  const [search, setSearch] = useState("");

  const params = { start_date: startDate, end_date: endDate };
  // All three, so the band can summarise the range whichever tab is open.
  // Driver Pay is admin / office staff only (PAY_READ); for anyone else it is
  // refused and says so, and its band figures are dashes.
  const bolQuery = useQuery({
    queryKey: ["trucking-report", "bol", startDate, endDate],
    queryFn: () => apiClient.get<TruckingReportEnvelope<TruckingBolSummaryRow>>("/trucking/reports/bol-summary", { params }).then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toBol)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const payQuery = useQuery({
    queryKey: ["trucking-report", "driver_pay", startDate, endDate],
    queryFn: () => apiClient.get<TruckingReportEnvelope<TruckingDriverPayRow>>("/trucking/reports/driver-pay", { params }).then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toPay)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const customerQuery = useQuery({
    queryKey: ["trucking-report", "customer_loads", startDate, endDate],
    queryFn: () => apiClient.get<TruckingReportEnvelope<TruckingCustomerRevenueRow>>("/trucking/reports/customer-revenue", { params }).then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toCustomer)),
    staleTime: 30_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const failedOf = (q: typeof bolQuery | typeof payQuery | typeof customerQuery) => queryLoadFailure(q) != null;
  const loadingOf = (q: typeof bolQuery | typeof payQuery | typeof customerQuery) => q.isLoading && !failedOf(q);

  const bolRows = failedOf(bolQuery) ? undefined : bolQuery.data;
  const payRows = failedOf(payQuery) ? undefined : payQuery.data;
  const customerRows = failedOf(customerQuery) ? undefined : customerQuery.data;

  const active = tab === "bol" ? bolQuery : tab === "driver_pay" ? payQuery : customerQuery;
  const activeRows = (tab === "bol" ? bolRows : tab === "driver_pay" ? payRows : customerRows) as
    | (BolSummaryRow | DriverPayRow | CustomerLoadsRow)[]
    | undefined;

  const filtered = useMemo(() => {
    const rows = activeRows ?? [];
    if (search === "") return rows;
    const q = search.toLowerCase();
    return rows.filter((row) => Object.values(row).some((v) => typeof v === "string" && v.toLowerCase().includes(q)));
  }, [activeRows, search]);

  const subjectOf: Record<ReportTab, string> = {
    bol: "the BOL summary",
    driver_pay: "driver pay",
    customer_loads: "customer revenue",
  };

  const tabClass = (on: boolean) =>
    `-mb-px border-b-2 px-1 pb-2 text-sm font-extrabold transition-colors ${
      on ? "border-fi-amber text-fieldiq-text" : "border-transparent text-fieldiq-muted hover:text-fieldiq-text"
    }`;

  return (
    <div data-testid="trucking-reports-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Trucking reports"
        title="Trucking Reports"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          {
            icon: <Truck className="size-4" aria-hidden="true" />,
            label: "Scheduled",
            sub: "Loads in range",
            tone: "neutral",
            loading: loadingOf(bolQuery),
            value: displayValue(total(bolRows as unknown as { [k: string]: unknown }[] | undefined, "total_loads")),
          },
          {
            icon: <PackageCheck className="size-4" aria-hidden="true" />,
            label: "Delivered",
            sub: "Delivered or signed",
            tone: "success",
            loading: loadingOf(customerQuery),
            value: displayValue(total(customerRows as unknown as { [k: string]: unknown }[] | undefined, "load_count")),
          },
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            // The drivers with pay rows in whole periods of the range, not the
            // roster: "On payroll" read 0 beside five active drivers. No wider
            // than the old sub-line (the band's third column, CLAUDE.md).
            label: "Drivers",
            sub: "With pay",
            tone: "info",
            loading: loadingOf(payQuery),
            value: displayValue(payRows ? payRows.length : null),
          },
          {
            icon: <Wallet className="size-4" aria-hidden="true" />,
            label: "Gross Pay",
            sub: "Whole periods only",
            tone: "accent",
            loading: loadingOf(payQuery),
            value: payRows ? fmtCurrency(sumMoney(payRows.map((r) => r.total_gross_pay))) : "—",
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Tabs, not chips: each one is a different report with its own scope. */}
          <div role="tablist" aria-label="Reports" className="flex gap-5 border-b border-fieldiq-border">
            {TABS.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} className={tabClass(tab === t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <label className="flex items-center gap-2 text-xs text-fieldiq-muted">
              From
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-fieldiq-muted">
              To
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none"
              />
            </label>
            <div className="relative sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                placeholder={tab === "driver_pay" ? "Filter by driver..." : "Filter by customer..."}
                aria-label="Filter rows"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </div>
          </div>

          <section
            aria-labelledby="report-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                {TABS.find((t) => t.key === tab)!.label}
              </p>
              <h2 id="report-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                {tab === "bol" ? "Loads by customer" : tab === "driver_pay" ? "Earnings by driver" : "Revenue by customer"}
              </h2>
            </header>

            {failedOf(active) ? (
              <LoadFailureNotice
                error={queryFailureError(active)}
                subject={subjectOf[tab]}
                allowed={tab === "driver_pay" ? "admins and office staff" : undefined}
              />
            ) : loadingOf(active) ? (
              <div className="space-y-2 p-4" aria-hidden="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">
                {(activeRows ?? []).length === 0 ? "Nothing in this date range" : "No rows match the filter"}
              </p>
            ) : (
              <div className="overflow-x-auto">
                {tab === "bol" ? (
                  <BolSummaryTable data={filtered as BolSummaryRow[]} />
                ) : tab === "driver_pay" ? (
                  <DriverPayTable data={filtered as DriverPayRow[]} />
                ) : (
                  <CustomerLoadsTable data={filtered as CustomerLoadsRow[]} />
                )}
              </div>
            )}

            {/* Scope footers: what each report counts, which the reader would otherwise get wrong. */}
            <p data-testid="report-scope" className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
              {tab === "bol"
                ? "Every load scheduled in the range, cancelled loads included. Delivered value counts delivered and signed loads only, and is not shown for a customer with a delivered load that has no rate."
                : tab === "driver_pay"
                  ? "Pay periods that start and end inside these dates only. A period that straddles either date is not included."
                  : "Loads delivered or signed in the range. Revenue is not shown for a customer with a load that has no rate."}
            </p>
          </section>
        </div>
      </section>
    </div>
  );
};

const th = "px-4 py-3 text-xs font-bold uppercase tracking-wider text-fieldiq-muted";

/** A money figure, or a dash with the API's count of loads that have no rate. */
const MoneyCell = ({ value, unrated, className, noun = LOADS }: { value: number | null; unrated: number | null; className: string; noun?: { one: string; many: string } }) => {
  const note = value === null ? missingRateNote(unrated, noun) : null;
  return (
    <td className={className}>
      {fmtCurrency(value)}
      {note && <span className="block text-xs font-normal text-fieldiq-muted">{note}</span>}
    </td>
  );
};
const td = "px-4 py-3 text-sm tabular-nums text-fieldiq-text";
const DELIVERED_LOADS = { one: "delivered load", many: "delivered loads" };

const BolSummaryTable = ({ data }: { data: BolSummaryRow[] }) => (
  <table className="w-full min-w-[880px] text-left">
    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
      <tr>
        <th className={th}>Customer</th>
        <th className={`${th} text-right`}>Loads scheduled</th>
        <th className={`${th} text-right`}>Cancelled</th>
        <th className={`${th} text-right`}>Delivered</th>
        <th className={`${th} text-right`}>Net weight ticketed</th>
        <th className={`${th} text-right`}>Delivered value</th>
      </tr>
    </thead>
    <tbody>
      {data.map((row, i) => (
        <tr key={`${row.customer_name}-${i}`} data-testid="bol-summary-row" className="border-t border-fieldiq-border">
          <td className={`${td} font-bold`}>{row.customer_name ?? "—"}</td>
          <td className={`${td} text-right`}>{displayValue(row.total_loads)}</td>
          <td className={`${td} text-right`}>{displayValue(row.cancelled_loads)}</td>
          <td className={`${td} text-right`}>{displayValue(row.delivered_loads)}</td>
          <td className={`${td} text-right`}>{row.total_weight == null ? "—" : `${fmtQty(row.total_weight)} kg`}</td>
          <MoneyCell value={row.total_value} unrated={row.delivered_unrated_count} noun={DELIVERED_LOADS} className={`${td} text-right font-extrabold`} />
        </tr>
      ))}
    </tbody>
  </table>
);

const DriverPayTable = ({ data }: { data: DriverPayRow[] }) => (
  <table className="w-full min-w-[1040px] text-left">
    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
      <tr>
        <th className={th}>Driver</th>
        <th className={`${th} text-right`}>Regular hrs</th>
        <th className={`${th} text-right`}>OT hrs</th>
        <th className={`${th} text-right`}>BOL earnings</th>
        <th className={`${th} text-right`}>Hourly earnings</th>
        <th className={`${th} text-right`}>Shop</th>
        <th className={`${th} text-right`}>Standby</th>
        <th className={`${th} text-right`}>Per diem</th>
        <th className={`${th} text-right`}>Gross pay</th>
      </tr>
    </thead>
    <tbody>
      {data.map((row) => (
        <tr key={row.driver_id} className="border-t border-fieldiq-border">
          <td className={`${td} font-bold`}>{row.driver_name ?? "—"}</td>
          <td className={`${td} text-right`}>{fmtHours(row.total_regular_hours, 1)}</td>
          <td className={`${td} text-right`}>{fmtHours(row.total_ot_hours, 1)}</td>
          <td className={`${td} text-right`}>{fmtCurrency(row.total_bol_earnings)}</td>
          <td className={`${td} text-right`}>{fmtCurrency(row.total_hourly_earnings)}</td>
          <td className={`${td} text-right`}>{fmtCurrency(row.total_shop_earnings)}</td>
          <td className={`${td} text-right`}>{fmtCurrency(row.total_standby_earnings)}</td>
          <td className={`${td} text-right`}>{fmtCurrency(row.total_per_diem)}</td>
          <td className={`${td} text-right font-extrabold`}>{fmtCurrency(row.total_gross_pay)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CustomerLoadsTable = ({ data }: { data: CustomerLoadsRow[] }) => (
  <table className="w-full min-w-[640px] text-left">
    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
      <tr>
        <th className={th}>Customer</th>
        <th className={`${th} text-right`}>Loads delivered or signed</th>
        <th className={`${th} text-right`}>Revenue</th>
        <th className={`${th} text-right`}>Avg per load</th>
      </tr>
    </thead>
    <tbody>
      {data.map((row, i) => (
        <tr key={`${row.customer_name}-${i}`} data-testid="customer-revenue-row" className="border-t border-fieldiq-border">
          <td className={`${td} font-bold`}>{row.customer_name ?? "—"}</td>
          <td className={`${td} text-right`}>{displayValue(row.load_count)}</td>
          <MoneyCell value={row.total_revenue} unrated={row.unrated_count} className={`${td} text-right font-extrabold`} />
          <td className={`${td} text-right`}>{fmtCurrency(row.avg_per_load)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default TruckingReportsScreen;
